import React, { useEffect, useMemo } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';

import SvgLock from 'assets/images/svgs/Player/ico_lock.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import { COLOR } from 'utils/colors';
import { isWeb } from 'utils/responsiveness';

import Text, { ETextCategory, ETextColor } from 'components/Text';
import TextWithBoldPhoneNumbers from 'components/TextWithBoldPhoneNumbers';

import { PlayerError } from '../../types';
import styles from './styles';

const ERR_PLAYER_GENERIC_MESSAGE = 'ERR_PLAYER_GENERIC_MESSAGE';

const { ERROR_CODE } = LANGUAGE_KEYS;

type Props = {
    error: PlayerError;
    fullscreen: boolean;
    boldTitle: boolean;
};

function Error({ error, fullscreen, boldTitle }: Props) {
    const { t } = useTranslation();

    const errorViewRef = React.useRef<View | null>(null);

    const accessibilityLabel = useMemo(() => {
        let label = '';

        if (error.message) {
            label = `${label}. ${error.message}`;
        }

        if ('description' in error) {
            label = `${label}. ${t(
                error.description || ERR_PLAYER_GENERIC_MESSAGE,
            )}`;
        }

        if (error.code) {
            label = `${label}. ${t(ERROR_CODE)} ${error.code}`;
        }

        return label;
    }, [error, t]);

    useEffect(() => {
        if (errorViewRef.current) {
            setAccessibilityFocusTo(errorViewRef);
        }
    }, []);

    return (
        <View
            ref={errorViewRef}
            style={fullscreen ? styles.rootFullscreen : styles.rootEmbedded}
            accessible
            accessibilityLabel={accessibilityLabel}
        >
            <View style={!fullscreen && styles.container}>
                {!!error.message && (
                    <View style={styles.titleContainer}>
                        {!!error.withIcon && (
                            <SvgLock
                                fill={COLOR.NEUTRAL}
                                style={styles.icon}
                                width={20}
                                height={20}
                            />
                        )}
                        <Text
                            category={
                                fullscreen
                                    ? ETextCategory.title3
                                    : ETextCategory.body1
                            }
                            color={ETextColor.neutral}
                            bold={boldTitle}
                            {...(isWeb && { tabIndex: 0 })}
                            center
                        >
                            {t(error.message)}
                        </Text>
                    </View>
                )}
                {'description' in error && (
                    <TextWithBoldPhoneNumbers
                        style={[
                            styles.description,
                            fullscreen && styles.transparentText,
                        ]}
                        category={
                            fullscreen
                                ? ETextCategory.body1
                                : ETextCategory.caption1
                        }
                        color={
                            fullscreen
                                ? ETextColor.neutral
                                : ETextColor.light_grey
                        }
                        text={t(
                            error.description || ERR_PLAYER_GENERIC_MESSAGE,
                        )}
                    />
                )}
                {!!error.code && (
                    <Text
                        style={[
                            styles.description,
                            fullscreen && styles.transparentText,
                        ]}
                        category={
                            fullscreen
                                ? ETextCategory.body2
                                : ETextCategory.caption2
                        }
                        color={
                            fullscreen
                                ? ETextColor.neutral
                                : ETextColor.light_grey
                        }
                        // @ts-ignore
                        tabIndex={0}
                    >{`${t(ERROR_CODE)} ${error.code}`}</Text>
                )}
            </View>
        </View>
    );
}

export default Error;
